import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { useInsightPost, useOtherAnalysis } from '../hooks/useInsights';
import { getSlugFromSymbol, CATEGORY_MAP } from '../config/categoryMapping';
import { trackScrollDepth, trackFeatureUsage, trackOutboundClick } from '../utils/analytics';
import { rewriteR2Url } from '../utils/r2Url';
import { extractTweetId } from '../utils/tweetUtils';

const BASE_URL = 'https://tradermap.io';
import TweetEmbed from '../components/TweetEmbed/TweetEmbed';
import TradesPromo from '../components/TradesPromo/TradesPromo';
import './InsightDetailPage.css';

// Category color mapping
const CATEGORY_COLORS = {
  BTC: { bg: 'rgba(247, 147, 26, 0.15)', color: '#f7931a', border: 'rgba(247, 147, 26, 0.3)' },
  ETH: { bg: 'rgba(98, 126, 234, 0.15)', color: '#627eea', border: 'rgba(98, 126, 234, 0.3)' },
  SOL: { bg: 'rgba(153, 69, 255, 0.15)', color: '#9945ff', border: 'rgba(153, 69, 255, 0.3)' },
  XRP: { bg: 'rgba(35, 41, 47, 0.3)', color: '#fff', border: 'rgba(255, 255, 255, 0.2)' },
  DOGE: { bg: 'rgba(186, 155, 53, 0.15)', color: '#ba9b35', border: 'rgba(186, 155, 53, 0.3)' },
  ADA: { bg: 'rgba(0, 51, 173, 0.15)', color: '#0033ad', border: 'rgba(0, 51, 173, 0.3)' },
  DEFAULT: { bg: 'rgba(167, 139, 250, 0.15)', color: '#a78bfa', border: 'rgba(167, 139, 250, 0.3)' }
};

function getCategoryStyle(category) {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.DEFAULT;
}

// Locale mapping for date formatting
const DATE_LOCALES = { en: 'en-US', ru: 'ru-RU', ko: 'ko-KR', zh: 'zh-CN' };

// Format date for display with locale support
function formatDate(dateString, lang = 'en') {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(DATE_LOCALES[lang] || 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Related Article Card
function RelatedCard({ post }) {
  const { localizedPath, language } = useLanguage();
  // Get tags (fallback to category)
  const tags = post.tags || [post.category];
  const primaryTag = tags[0] || post.category;
  const categoryStyle = getCategoryStyle(primaryTag);

  const handleRelatedClick = () => {
    trackFeatureUsage('blog_related_click', {
      slug: post.slug,
      title: post.title,
      category: primaryTag
    });
  };

  return (
    <Link to={localizedPath(`/insights/${post.slug}`)} className="related-card" onClick={handleRelatedClick}>
      {(post.coverImageVariants?.thumbnail || post.coverImage) && (
        <div className="related-card-image">
          <img src={rewriteR2Url(post.coverImageVariants?.thumbnail || post.coverImage)} alt={post.title} loading="lazy" />
        </div>
      )}
      <div className="related-card-content">
        <div className="related-tags-row">
          {tags.slice(0, 2).map(tag => {
            const style = getCategoryStyle(tag);
            const isCoin = !!CATEGORY_MAP[tag];
            const badgeStyle = {
              backgroundColor: style.bg,
              color: style.color
            };
            return isCoin ? (
              <Link
                key={tag}
                to={localizedPath(`/price/${tag}`)}
                className="related-category-badge related-tag-link"
                style={badgeStyle}
                onClick={(e) => e.stopPropagation()}
              >
                {tag}
              </Link>
            ) : (
              <span
                key={tag}
                className="related-category-badge"
                style={badgeStyle}
              >
                {tag}
              </span>
            );
          })}
        </div>
        <h4 className="related-card-title">{post.title}</h4>
        <span className="related-card-date">{formatDate(post.publishedAt, language)}</span>
      </div>
    </Link>
  );
}

function InsightDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localizedPath } = useLanguage();

  const { language } = useLanguage();

  const { post, related, loading, error } = useInsightPost(slug, language);
  const { posts: otherPosts } = useOtherAnalysis(slug, 4, language);

  // BreadcrumbList JSON-LD
  useEffect(() => {
    if (!post) return;

    const primaryTag = (post.tags && post.tags[0]) || post.category;
    const categorySlug = getSlugFromSymbol(primaryTag) || primaryTag.toLowerCase();

    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Insights", "item": `${BASE_URL}/insights` },
        { "@type": "ListItem", "position": 2, "name": primaryTag, "item": `${BASE_URL}/insights/${categorySlug}` },
        { "@type": "ListItem", "position": 3, "name": post.title }
      ]
    };

    let script = document.querySelector('script[data-schema="breadcrumb"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-schema', 'breadcrumb');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(breadcrumbLd);

    return () => {
      const el = document.querySelector('script[data-schema="breadcrumb"]');
      if (el) el.remove();
    };
  }, [post]);

  // Track scroll depth milestones
  const scrollMilestones = useRef({ 25: false, 50: false, 75: false, 90: false });

  useEffect(() => {
    if (loading || !post) return;

    // Reset milestones on article change
    scrollMilestones.current = { 25: false, 50: false, 75: false, 90: false };

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;

      const scrolled = Math.round((window.scrollY / scrollHeight) * 100);

      // Track each milestone only once
      [25, 50, 75, 90].forEach(milestone => {
        if (scrolled >= milestone && !scrollMilestones.current[milestone]) {
          scrollMilestones.current[milestone] = true;
          trackScrollDepth(milestone, `/insights/${slug}`);
        }
      });
    };

    // Throttle scroll handler
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [slug, loading, post]);

  // Memoize ReactMarkdown components to prevent TweetEmbed remounts
  const markdownComponents = useMemo(() => ({
    h1: ({ node, ...props }) => <h2 {...props} />,
    h2: ({ node, ...props }) => <h2 {...props} />,
    h3: ({ node, ...props }) => <h3 {...props} />,
    p: ({ node, children, ...props }) => {
      const childArray = React.Children.toArray(children);
      if (childArray.length === 1) {
        const child = childArray[0];
        if (typeof child === 'string') {
          const tweetId = extractTweetId(child.trim());
          if (tweetId) return <TweetEmbed tweetId={tweetId} url={child.trim()} />;
        }
        if (React.isValidElement(child) && child.props?.href) {
          const tweetId = extractTweetId(child.props.href);
          if (tweetId) return <TweetEmbed tweetId={tweetId} url={child.props.href} />;
        }
      }
      return <p {...props}>{children}</p>;
    },
    a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
    img: ({ node, src, ...props }) => (
      <figure className="article-image">
        <img {...props} src={rewriteR2Url(src)} loading="lazy" />
      </figure>
    ),
    blockquote: ({ node, ...props }) => <blockquote {...props} />,
    code: ({ node, inline, ...props }) =>
      inline ? <code {...props} /> : <pre><code {...props} /></pre>,
    table: ({ node, ...props }) => <table {...props} />,
    thead: ({ node, ...props }) => <thead {...props} />,
    tbody: ({ node, ...props }) => <tbody {...props} />,
    tr: ({ node, ...props }) => <tr {...props} />,
    th: ({ node, ...props }) => <th {...props} />,
    td: ({ node, ...props }) => <td {...props} />
  }), []);

  // Loading state
  if (loading) {
    return (
      <>
        <Header />
        <main className="insight-detail-page">
          <div className="insight-detail-loading">
            <div className="loading-spinner"></div>
            <p>Loading article...</p>
          </div>
        </main>
      </>
    );
  }

  // Error state — service error (503/network)
  if (error === 'service_error') {
    return (
      <>
        <Header />
        <main className="insight-detail-page">
          <div className="insight-detail-error">
            <h2>Temporarily Unavailable</h2>
            <p>We're having trouble loading this article. Please try again in a moment.</p>
            <button
              className="back-to-insights-btn"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
            <Link to={localizedPath('/insights')} className="back-to-insights-btn" style={{ marginLeft: 12 }}>
              Back to Insights
            </Link>
          </div>
        </main>
      </>
    );
  }

  // Error state — not found (404)
  if (error || !post) {
    return (
      <>
        <Header />
        <main className="insight-detail-page">
          <div className="insight-detail-error">
            <h2>Article Not Found</h2>
            <p>The article you're looking for doesn't exist or has been removed.</p>
            <Link to={localizedPath('/insights')} className="back-to-insights-btn">
              Back to Insights
            </Link>
          </div>
        </main>
      </>
    );
  }

  // Get tags (fallback to category)
  const tags = post.tags || [post.category];
  const primaryTag = tags[0] || post.category;
  const categoryStyle = getCategoryStyle(primaryTag);

  return (
    <>
      <SEO
        page="insightDetail"
        dynamicParams={{ slug: post.slug }}
        title={`${post.title} | Tradermap Insights`}
        description={post.excerpt || `Read our analysis on ${tags.join(', ')} and cryptocurrency market insights.`}
        image={rewriteR2Url(post.coverImageVariants?.og || post.coverImage)}
        keywords={`${tags.join(', ')}, crypto analysis, trading insights, ${post.title.toLowerCase()}`}
        articleData={{
          title: post.title,
          excerpt: post.excerpt,
          coverImage: rewriteR2Url(post.coverImageVariants?.original || post.coverImage),
          authorName: post.authorName || 'TraderMap Team',
          author: post.author || null,
          publishedAt: post.publishedAt,
          updatedAt: post.updatedAt
        }}
      />

      <Header />

      <main className="insight-detail-page">
        <TradesPromo />
        <article className="insight-article">
          {/* Breadcrumb */}
          <div className="insight-breadcrumb">
            <Link to={localizedPath('/insights')}>Insights</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to={localizedPath(`/insights/${getSlugFromSymbol(primaryTag) || primaryTag.toLowerCase()}`)}>{primaryTag}</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{post.title}</span>
          </div>

          {/* Header */}
          <header className="insight-article-header">
            <div className="insight-article-tags">
              {tags.map(tag => {
                const style = getCategoryStyle(tag);
                const isCoin = !!CATEGORY_MAP[tag];
                const badgeStyle = {
                  backgroundColor: style.bg,
                  color: style.color,
                  borderColor: style.border
                };
                return isCoin ? (
                  <Link
                    key={tag}
                    to={localizedPath(`/price/${tag}`)}
                    className="insight-article-category insight-tag-link"
                    style={badgeStyle}
                  >
                    {tag}
                  </Link>
                ) : (
                  <span
                    key={tag}
                    className="insight-article-category"
                    style={badgeStyle}
                  >
                    {tag}
                  </span>
                );
              })}
            </div>

            <h1 className="insight-article-title">{post.title}</h1>

            {post.excerpt && (
              <p className="insight-article-excerpt">{post.excerpt}</p>
            )}

            <div className="insight-article-meta">
              <div className="insight-author">
                {post.author && post.author.avatar ? (
                  <img src={rewriteR2Url(post.author.avatar)} alt={post.author.name} className="insight-author-avatar" />
                ) : (
                  <div className="insight-author-avatar">
                    {((post.author && post.author.name) || post.authorName || 'T').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="insight-author-info">
                  <span className="insight-author-name">{(post.author && post.author.name) || post.authorName || 'TraderMap Team'}</span>
                  <span className="insight-publish-date">{formatDate(post.publishedAt, language)}</span>
                </div>
              </div>
              {post.author && post.author.description && (
                <div className="insight-author-description">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                      p: ({ node, ...props }) => <p {...props} />
                    }}
                  >
                    {post.author.description}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </header>

          {/* Cover Image */}
          {(post.coverImageVariants?.original || post.coverImage) && (
            <div className="insight-article-cover">
              <img src={rewriteR2Url(post.coverImageVariants?.original || post.coverImage)} alt={post.title} loading="lazy" />
            </div>
          )}

          {/* Content */}
          <div className="insight-article-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Share Section */}
          <div className="insight-article-share">
            <span>Share this article:</span>
            <div className="share-buttons">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn twitter"
                onClick={() => trackOutboundClick('twitter.com', 'share_article', { article_slug: post.slug })}
              >
                Twitter
              </a>
              <button
                className="share-btn copy"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  trackFeatureUsage('copy_article_link', { article_slug: post.slug });
                  alert('Link copied!');
                }}
              >
                Copy Link
              </button>
            </div>
          </div>
        </article>

        {/* Other Analysis - Random Fresh Posts */}
        {otherPosts && otherPosts.length > 0 && (
          <section className="insight-related other-analysis">
            <div className="insight-related-header">
              <h3>Other Analysis</h3>
              <Link to={localizedPath('/insights')} className="view-more-link">
                View All
              </Link>
            </div>
            <div className="insight-related-grid">
              {otherPosts.map(otherPost => (
                <RelatedCard key={otherPost._id || otherPost.slug} post={otherPost} />
              ))}
            </div>
          </section>
        )}

        {/* Back to Insights */}
        <div className="insight-back-nav">
          <Link to={localizedPath('/insights')} className="back-to-insights-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to All Insights
          </Link>
        </div>
      </main>
    </>
  );
}

export default InsightDetailPage;
